import { useState, useEffect, useCallback } from "react";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import {
  BarChart3, TrendingUp, DollarSign, Package, Users,
  FlaskConical, Download, Calendar,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";

const BAR_COLORS = ["#10b981", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#ec4899", "#84cc16"];

const MiniBar = ({ data, maxVal, color }) => (
  <div className="h-20 flex items-end gap-[2px]">
    {data.map((v, i) => (
      <div key={i} className="flex-1 rounded-t" style={{ height: `${maxVal > 0 ? (v / maxVal) * 100 : 0}%`, backgroundColor: color, minHeight: v > 0 ? "3px" : "0" }} title={`${v}`} />
    ))}
  </div>
);

const HorizontalBar = ({ label, value, maxVal, color, suffix = "" }) => (
  <div className="flex items-center gap-3">
    <span className="text-xs text-slate-400 w-28 truncate text-right">{label}</span>
    <div className="flex-1 bg-slate-700/50 rounded-full h-5 overflow-hidden">
      <div className="h-full rounded-full transition-all duration-700 flex items-center px-2" style={{ width: `${maxVal > 0 ? Math.max(5, (value / maxVal) * 100) : 0}%`, backgroundColor: color }}>
        <span className="text-[10px] text-white font-mono whitespace-nowrap">{typeof value === "number" ? value.toLocaleString("en-IN") : value}{suffix}</span>
      </div>
    </div>
  </div>
);

const DonutChart = ({ data, colors, size = 80 }) => {
  const total = data.reduce((s, d) => s + d.value, 0);
  let acc = 0;
  const segments = data.map((d, i) => {
    const pct = total > 0 ? (d.value / total) * 100 : 0;
    const offset = acc;
    acc += pct;
    return { ...d, pct, offset, color: colors[i % colors.length] };
  });
  const r = size / 2 - 4;
  const c = Math.PI * 2 * r;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#1e293b" strokeWidth="8" />
        {segments.map((s, i) => (
          <circle key={i} cx={size/2} cy={size/2} r={r} fill="none" stroke={s.color} strokeWidth="8"
            strokeDasharray={`${(s.pct / 100) * c} ${c}`}
            strokeDashoffset={-((s.offset / 100) * c)}
            transform={`rotate(-90 ${size/2} ${size/2})`} className="transition-all duration-700" />
        ))}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-white font-bold text-sm">{total}</span>
      </div>
    </div>
  );
};

const ReportsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("month");
  const [unit, setUnit] = useState("all");

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const unitParam = unit !== "all" ? `&unit=${unit}` : "";
      const res = await authFetch(`${API}/factory/reports?period=${period}${unitParam}`);
      const d = await res.json();
      if (d.success) setData(d.data);
    } catch { toast.error("Failed to load reports"); }
    finally { setLoading(false); }
  }, [period, unit]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const downloadCSV = (rows, headers, filename) => {
    const csv = [headers.join(","), ...rows.map(r => headers.map(h => r[h] ?? "").join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${filename}`);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;

  const d = data || {};
  const prodTrends = d.production_trends || [];
  const dispatchSummary = d.dispatch_summary || {};
  const expenseBreakdown = d.expense_breakdown || [];
  const materialSummary = d.material_summary || [];
  const manpowerTrend = d.manpower_trend || [];
  const qcResults = d.qc_results || {};

  const maxProd = Math.max(...prodTrends.map(p => p.actual), 1);
  const maxExpense = Math.max(...expenseBreakdown.map(e => e.total), 1);
  const maxMaterial = Math.max(...materialSummary.map(m => m.total_qty), 1);

  const dispatchData = Object.entries(dispatchSummary).map(([k, v]) => ({ label: k, value: v }));
  const dispatchColors = dispatchData.map(d => {
    const c = { PENDING: "#64748b", WAITING: "#f59e0b", LOADING: "#3b82f6", READY: "#06b6d4", DISPATCHED: "#10b981", CANCELLED: "#ef4444" };
    return c[d.label] || "#94a3b8";
  });
  const qcData = Object.entries(qcResults).map(([k, v]) => ({ label: k, value: v }));
  const qcColors = qcData.map(d => d.label === "PASS" ? "#10b981" : d.label === "FAIL" ? "#ef4444" : "#f59e0b");

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Sora']">Factory Reports</h1>
          <p className="text-emerald-600 text-sm mt-1">Analytics and infographics for factory operations</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32 bg-[#1C2B2B] border-emerald-900/50 text-white" data-testid="period-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
            </SelectContent>
          </Select>
          <Select value={unit} onValueChange={setUnit}>
            <SelectTrigger className="w-32 bg-[#1C2B2B] border-emerald-900/50 text-white" data-testid="unit-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Units</SelectItem>
              <SelectItem value="Unit 1">Unit 1</SelectItem>
              <SelectItem value="Unit 2">Unit 2</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Production Trends */}
        <Card className="bg-[#1C2B2B] border-emerald-900/30 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-400" /> Production Trends</h3>
            {prodTrends.length > 0 && (
              <Button size="sm" variant="ghost" className="text-emerald-400 text-xs h-7" onClick={() => downloadCSV(prodTrends, ["date", "actual", "planned", "efficiency"], "production_report.csv")} data-testid="download-production">
                <Download className="w-3 h-3 mr-1" /> CSV
              </Button>
            )}
          </div>
          {prodTrends.length > 0 ? (
            <>
              <MiniBar data={prodTrends.map(p => p.actual)} maxVal={maxProd} color="#10b981" />
              <div className="flex justify-between mt-1">
                <span className="text-[9px] text-slate-500">{prodTrends[0]?.date}</span>
                <span className="text-[9px] text-slate-500">{prodTrends[prodTrends.length - 1]?.date}</span>
              </div>
              <div className="flex items-center gap-4 mt-3">
                <span className="text-xs text-slate-400">Total: <span className="text-emerald-400 font-mono">{prodTrends.reduce((s, p) => s + p.actual, 0).toLocaleString()} MT</span></span>
                <span className="text-xs text-slate-400">Avg Eff: <span className="text-emerald-400 font-mono">{(prodTrends.reduce((s, p) => s + p.efficiency, 0) / prodTrends.length).toFixed(1)}%</span></span>
              </div>
            </>
          ) : <p className="text-slate-500 text-sm py-8 text-center">No production data for this period</p>}
        </Card>

        {/* Dispatch Summary */}
        <Card className="bg-[#1C2B2B] border-emerald-900/30 p-5">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Package className="w-4 h-4 text-blue-400" /> Dispatch Summary</h3>
          {dispatchData.length > 0 ? (
            <div className="flex items-center gap-6">
              <DonutChart data={dispatchData} colors={dispatchColors} size={100} />
              <div className="space-y-2 flex-1">
                {dispatchData.map((d, i) => (
                  <div key={d.label} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: dispatchColors[i] }} />
                    <span className="text-xs text-slate-300 flex-1">{d.label}</span>
                    <span className="text-xs text-white font-mono">{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : <p className="text-slate-500 text-sm py-8 text-center">No dispatch data</p>}
        </Card>

        {/* Expense Breakdown */}
        <Card className="bg-[#1C2B2B] border-emerald-900/30 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold flex items-center gap-2"><DollarSign className="w-4 h-4 text-rose-400" /> Expense Breakdown</h3>
            {expenseBreakdown.length > 0 && (
              <Button size="sm" variant="ghost" className="text-emerald-400 text-xs h-7" onClick={() => downloadCSV(expenseBreakdown, ["category", "total", "count"], "expenses_report.csv")} data-testid="download-expenses">
                <Download className="w-3 h-3 mr-1" /> CSV
              </Button>
            )}
          </div>
          {expenseBreakdown.length > 0 ? (
            <div className="space-y-2">
              {expenseBreakdown.map((e, i) => (
                <HorizontalBar key={e.category} label={e.category} value={e.total} maxVal={maxExpense} color={BAR_COLORS[i % BAR_COLORS.length]} suffix=" Rs." />
              ))}
              <div className="pt-2 border-t border-emerald-900/30">
                <span className="text-xs text-slate-400">Total: <span className="text-rose-400 font-mono font-medium">Rs. {(d.total_expenses || 0).toLocaleString("en-IN")}</span></span>
              </div>
            </div>
          ) : <p className="text-slate-500 text-sm py-8 text-center">No expenses recorded</p>}
        </Card>

        {/* Material Inflow */}
        <Card className="bg-[#1C2B2B] border-emerald-900/30 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold flex items-center gap-2"><BarChart3 className="w-4 h-4 text-amber-400" /> Material Inflow</h3>
            {materialSummary.length > 0 && (
              <Button size="sm" variant="ghost" className="text-emerald-400 text-xs h-7" onClick={() => downloadCSV(materialSummary, ["material", "total_qty", "total_cost", "entries"], "materials_report.csv")} data-testid="download-materials">
                <Download className="w-3 h-3 mr-1" /> CSV
              </Button>
            )}
          </div>
          {materialSummary.length > 0 ? (
            <div className="space-y-2">
              {materialSummary.map((m, i) => (
                <HorizontalBar key={m.material} label={m.material} value={m.total_qty} maxVal={maxMaterial} color={BAR_COLORS[(i + 3) % BAR_COLORS.length]} suffix=" MT" />
              ))}
            </div>
          ) : <p className="text-slate-500 text-sm py-8 text-center">No material entries</p>}
        </Card>

        {/* Manpower Trend */}
        <Card className="bg-[#1C2B2B] border-emerald-900/30 p-5">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Users className="w-4 h-4 text-violet-400" /> Manpower Trend</h3>
          {manpowerTrend.length > 0 ? (
            <>
              <MiniBar data={manpowerTrend.map(m => m.total)} maxVal={Math.max(...manpowerTrend.map(m => m.total), 1)} color="#8b5cf6" />
              <div className="flex justify-between mt-1">
                <span className="text-[9px] text-slate-500">{manpowerTrend[0]?.date}</span>
                <span className="text-[9px] text-slate-500">{manpowerTrend[manpowerTrend.length - 1]?.date}</span>
              </div>
              <div className="mt-3">
                <span className="text-xs text-slate-400">Avg Workers/Day: <span className="text-violet-400 font-mono">{Math.round(manpowerTrend.reduce((s, m) => s + m.total, 0) / manpowerTrend.length)}</span></span>
              </div>
            </>
          ) : <p className="text-slate-500 text-sm py-8 text-center">No manpower data</p>}
        </Card>

        {/* QC Results */}
        <Card className="bg-[#1C2B2B] border-emerald-900/30 p-5">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><FlaskConical className="w-4 h-4 text-cyan-400" /> Quality Control Results</h3>
          {qcData.length > 0 ? (
            <div className="flex items-center gap-6">
              <DonutChart data={qcData} colors={qcColors} size={100} />
              <div className="space-y-2 flex-1">
                {qcData.map((q, i) => (
                  <div key={q.label} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: qcColors[i] }} />
                    <span className="text-xs text-slate-300 flex-1">{q.label}</span>
                    <span className="text-xs text-white font-mono">{q.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : <p className="text-slate-500 text-sm py-8 text-center">No QC data</p>}
        </Card>
      </div>
    </div>
  );
};

export default ReportsPage;
